import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ADPResourceService } from 'digital-core';
import { Certificate, Coverage, PlanDetail } from 'src/app/model/plan/plan-detail';
import { IServiceRequestProduct, SERVICE_REQUEST_PLACEHOLDER_ID_PREFIX } from 'src/app/model/plan/service-request-product';
import { LoggedInUserSettings } from './settings/logged-in-user-settings';

/** Owning-policy coordinates for a covered device type. */
export interface ICoveringCertificate {
  planNumber: string;
  dealerCode: string;
  certificateNumber: string;
}

/**
 * Cross-policy source of truth for the service-request flow.
 *
 * The flow used to read a single `$selectedPlanDetail`: one plan decided which request types
 * were offered, which device-type categories showed under a request type, and which products
 * showed under a category. A customer holding more than one policy could therefore only ever
 * file against whichever plan happened to be selected.
 *
 * This service widens all three steps to every policy the customer holds — Active, Expired, or
 * Cancelled — and tags each product with the plan and certificate that covers it so the
 * downstream GSSP handover and add/update-equipment calls stay plan-scoped. A policy's current
 * status doesn't gate claim eligibility here: a customer can still file against a lapsed or
 * cancelled policy as long as the date of loss falls within the period it was actually in force,
 * which is validated later (at claim submission), not at this device/category-picking step.
 *
 * Scope resolution, in order:
 *   1. `ServiceRequest_MultiPolicy_Enabled` off -> `[$selectedPlanDetail]` (pre-existing behavior).
 *   2. `$planDetails` empty -> `[$selectedPlanDetail]`. The multi-plan list is only populated when
 *      `Global_GetAll_PlanDetails_Enabled` or `Global_Bulk_PlanDetails_Enabled` is on, so clients
 *      without a bulk pipeline degrade to single-plan rather than to an empty flow.
 *   3. Otherwise -> every plan in `$planDetails`, regardless of status.
 */
@Injectable({ providedIn: 'root' })
export class ServiceRequestAggregationService {
  private readonly resourceService = inject(ADPResourceService);
  private readonly loggedInUserSettings = inject(LoggedInUserSettings);

  private readonly selectedPlanDetail = toSignal(this.loggedInUserSettings.$selectedPlanDetail, { initialValue: null });
  private readonly allPlanDetails = toSignal(this.loggedInUserSettings.$planDetails, { initialValue: null as PlanDetail[] | null });

  /** Read lazily rather than at construction: portal settings are not populated until the
   * site-resources load completes, which can happen after this root-provided service is built. */
  private get isMultiPolicyEnabled(): boolean {
    return this.resourceService.isPortalSettingTrue('ServiceRequest_MultiPolicy_Enabled');
  }

  /** When on, a coverage whose beginDate is still in the future is offered like any other - Elita
   * stays the decision-maker for claim eligibility, so the portal must not use effective dates to
   * decide what to display. */
  private readonly isFutureCoveragesEnabled = this.resourceService.isPortalSettingTrue('ServiceRequest_FutureCoverages_Enabled');

  /** Coverage type codes (comma-separated in CMS like the other ServiceRequest_*_Codes settings,
   * e.g. 'MB'; semicolons tolerated) the future-coverage flag applies to. Any other coverage keeps
   * the beginDate filter, so the flag can't widen what Elita hasn't agreed to show. */
  private readonly futureCoverageTypeCodes = (this.resourceService.getPortalSetting('ServiceRequest_FutureCoverages_CoverageTypeCodes')?.Value ?? '')
    .split(/[,;]/)
    .map((code) => code.trim())
    .filter(Boolean);

  /** Every policy whose coverage the flow should consider. See the class docblock for scoping. */
  readonly plansInScope = computed<PlanDetail[]>(() => {
    const selected = this.selectedPlanDetail();
    const singlePlanScope = selected ? [selected] : [];

    if (!this.isMultiPolicyEnabled) {
      return singlePlanScope;
    }

    const all = this.allPlanDetails();
    return all?.length ? all : singlePlanScope;
  });

  readonly isAggregatingMultiplePolicies = computed(() => this.plansInScope().length > 1);

  /** Union of every in-scope plan's `deviceTypeCodes` - drives request-type and category filtering. */
  readonly deviceTypeCodes = computed<string[]>(() => this.unionOf((plan) => plan.deviceTypeCodes));

  /** Union of every in-scope plan's coverage codes that have already begun (plus the future ones
   * allowed by ServiceRequest_FutureCoverages_Enabled) - drives repair/reimbursement filtering. */
  readonly coverageTypeCodes = computed<string[]>(() => this.unionOf((plan) => this.startedCoverageTypeCodes(plan)));

  /**
   * Every registered product across the in-scope policies, each tagged with its owning plan.
   *
   * Deduped by `deviceId` because a customer can hold overlapping policies that both report the
   * same registered unit; the first in-scope plan wins so the selection stays stable.
   */
  readonly products = computed<IServiceRequestProduct[]>(() => {
    const seenDeviceIds = new Set<string>();
    const products: IServiceRequestProduct[] = [];

    for (const plan of this.plansInScope()) {
      const certificatesByNumber = new Map((plan.certificates ?? []).map((certificate) => [certificate.certificateNumber, certificate]));

      for (const equipment of plan.equipments ?? []) {
        if (equipment.deviceId && seenDeviceIds.has(equipment.deviceId)) {
          continue;
        }
        if (equipment.deviceId) {
          seenDeviceIds.add(equipment.deviceId);
        }

        const certificateNumber = equipment.childCertificateNumber ?? plan.coreCertificate ?? '';
        products.push({
          ...equipment,
          planNumber: plan.planNumber,
          dealerCode: plan.dealerCode,
          certificateNumber,
          certificateProductDescription: certificatesByNumber.get(certificateNumber)?.product?.productDescription ?? '',
          isSingleCertPolicy: plan.isMultiCertPolicy !== true,
          isPlaceholder: false,
        });
      }
    }

    return products;
  });

  /**
   * Products to show for one device type: real equipment first, then placeholders.
   *
   * A single-cert policy can come back with a certificate that covers the device type but has
   * no equipment registered against it. There is nothing to render from `equipments`, so we fall
   * back to the certificate itself - matching on `product.allowedDeviceTypes` - and emit a
   * placeholder row. The UI renders those the way the dashboard renders an incomplete product,
   * and the customer supplies the missing details before the claim starts.
   */
  productsForDeviceType(deviceTypeCode: string): IServiceRequestProduct[] {
    if (!deviceTypeCode) {
      return [];
    }

    const registered = this.products().filter((product) => product.deviceTypeCode === deviceTypeCode);
    const untyped = this.untypedProductsAllowingDeviceType(deviceTypeCode, registered);
    return [...registered, ...untyped, ...this.placeholdersForDeviceType(deviceTypeCode, [...registered, ...untyped])];
  }

  /**
   * The covering certificate's `product.allowedDeviceTypes` for one product row — lets a caller
   * scope a "what type is this?" picker (the certificate-item-info dialog's Type dropdown) to
   * what this certificate actually allows, same as find-your-coverage.component.ts's
   * getAvailableDeviceTypes.
   */
  getAllowedDeviceTypesFor(product: IServiceRequestProduct): string[] {
    return this.findCertificateFor(product)?.product?.allowedDeviceTypes ?? [];
  }

  /**
   * Whether the customer can register a genuinely NEW item for this device type at all.
   *
   * A single-cert policy enumerates a fixed set of equipment/placeholder slots for a device
   * type - every one of them already shows up as its own row via productsForDeviceType (real
   * equipment, an untyped candidate, or a placeholder), so there's nothing left to add. Only a
   * multi-cert (MAPP) policy genuinely supports registering a brand-new item against a fresh
   * certificate, so "a different appliance" (the manual add-new-device flow) only makes sense
   * when one of those backs the type the customer is browsing.
   */
  canRegisterNewProductFor(deviceTypeCode: string): boolean {
    if (!deviceTypeCode) {
      return false;
    }
    return this.plansInScope().some(
      (plan) => plan.isMultiCertPolicy === true && (plan.certificates ?? []).some((certificate) => certificate.product?.allowedDeviceTypes?.includes(deviceTypeCode))
    );
  }

  /**
   * Whether some certificate that actually covers `deviceTypeCode` also carries
   * `coverageTypeCode`.
   *
   * Used to keep an appliance-category picker (Kitchen/Laundry/General) scoped to devices whose
   * OWN certificate supports the reimbursement/repair type the customer already picked - e.g. a
   * kitchen refrigerator's certificate typically carries food-spoilage (FL) coverage, but a water
   * heater's typically doesn't, even though both device types can otherwise land under the same
   * "Appliances" request-type bucket.
   *
   * Checked two ways: first by `product.allowedDeviceTypes` (covers a not-yet-registered
   * placeholder slot), then by resolving the OWNING certificate of any already-registered
   * product of this type via `certificateNumber` (same fallback as getAllowedDeviceTypesFor) -
   * some certificates (seen with tool/outdoor equipment like MOWR) don't literally list every
   * device type registered against them in allowedDeviceTypes, so relying on that alone would
   * wrongly exclude a device that's genuinely covered.
   */
  deviceTypeSupportsCoverage(deviceTypeCode: string, coverageTypeCode: string): boolean {
    if (!deviceTypeCode || !coverageTypeCode) {
      return false;
    }

    const hasCoverage = (certificate: Certificate | null | undefined): boolean => this.certificateHasStartedCoverage(certificate, coverageTypeCode, deviceTypeCode);

    const matchesViaAllowedTypes = this.plansInScope().some((plan) =>
      (plan.certificates ?? []).some((certificate) => certificate.product?.allowedDeviceTypes?.includes(deviceTypeCode) && hasCoverage(certificate))
    );

    if (matchesViaAllowedTypes) {
      return true;
    }

    return this.products()
      .filter((product) => product.deviceTypeCode === deviceTypeCode)
      .some((product) => hasCoverage(this.findCertificateFor(product)));
  }

  /**
   * Whether the ONE certificate owning `product` carries `coverageTypeCode`.
   *
   * deviceTypeSupportsCoverage answers "does any certificate for this device type?", which is too
   * coarse once a customer holds several policies: a MAPP certificate can carry the coverage while
   * an LPP certificate for the very same device type doesn't, and only the former should be
   * offered under a request type keyed on that coverage.
   *
   * `deviceTypeCode` is the category being browsed - used for the coverage's excludeDeviceTypes
   * check when the product itself is untyped (it only surfaced under this category because its
   * certificate allows it). The product's own deviceTypeCode wins when it has one.
   */
  productSupportsCoverage(product: IServiceRequestProduct, coverageTypeCode: string, deviceTypeCode?: string): boolean {
    if (!coverageTypeCode) {
      return true;
    }
    return this.certificateHasStartedCoverage(this.findCertificateFor(product), coverageTypeCode, product.deviceTypeCode || deviceTypeCode);
  }

  /**
   * A coverage whose beginDate is still in the future hasn't begun. A missing/unparseable
   * beginDate is treated as already begun, so only an explicit future date ever counts as pending.
   */
  isCoverageStarted(coverage: Coverage): boolean {
    const begin = coverage.beginDate ? new Date(coverage.beginDate).getTime() : NaN;
    return Number.isNaN(begin) || begin <= Date.now();
  }

  /**
   * Whether the flow should offer `coverage`: it has begun, or ServiceRequest_FutureCoverages_Enabled
   * is on and its type is listed in ServiceRequest_FutureCoverages_CoverageTypeCodes. A not-yet-begun
   * coverage otherwise must not make its request type (or a product under it) selectable.
   */
  private isCoverageOffered(coverage: Coverage): boolean {
    return this.isCoverageStarted(coverage) || (this.isFutureCoveragesEnabled && this.futureCoverageTypeCodes.includes(coverage.coverageTypeCode));
  }

  /**
   * Whether `coverage` can be offered for `deviceTypeCode`: it is offered (see isCoverageOffered)
   * and doesn't list the device type in excludeDeviceTypes (e.g. FL food-spoilage excludes TRCP
   * trash compactors even when the same certificate covers them for repair). No device type means
   * no exclusion check.
   */
  coverageAppliesTo(coverage: Coverage, deviceTypeCode?: string | null): boolean {
    return this.isCoverageOffered(coverage) && !(deviceTypeCode && coverage.excludeDeviceTypes?.includes(deviceTypeCode));
  }

  private certificateHasStartedCoverage(certificate: Certificate | null | undefined, coverageTypeCode: string, deviceTypeCode?: string | null): boolean {
    return !!certificate?.coverages?.some((coverage) => coverage.coverageTypeCode === coverageTypeCode && this.coverageAppliesTo(coverage, deviceTypeCode));
  }

  /**
   * plan.coverageTypeCodes minus codes that appear on the plan's certificates ONLY with a future
   * beginDate. Codes the certificates don't carry at all (e.g. the derived 'SP') are kept as-is.
   * With ServiceRequest_FutureCoverages_Enabled on, coverages listed in
   * ServiceRequest_FutureCoverages_CoverageTypeCodes are never pending.
   */
  private startedCoverageTypeCodes(plan: PlanDetail): string[] {
    const started = new Set<string>();
    const pending = new Set<string>();
    for (const coverage of (plan.certificates ?? []).flatMap((certificate) => certificate.coverages ?? [])) {
      (this.isCoverageOffered(coverage) ? started : pending).add(coverage.coverageTypeCode);
    }
    return (plan.coverageTypeCodes ?? []).filter((code) => started.has(code) || !pending.has(code));
  }

  /** In-scope plan by number - used to re-resolve the owning plan of a selected product. */
  findPlanByNumber(planNumber: string | undefined | null): PlanDetail | null {
    if (!planNumber) {
      return null;
    }
    return this.plansInScope().find((plan) => plan.planNumber === planNumber) ?? null;
  }

  /**
   * First certificate covering `deviceTypeCode`, searched across the in-scope policies —
   * regardless of the certificate's current status, since a lapsed/cancelled certificate can
   * still be the right one to file against for a loss that occurred while it was in force.
   *
   * Widens the single-plan lookup the flow used before: a plan holds a CORE certificate covering
   * the default device types plus non-core certificates from purchased add-ons, each with its own
   * `allowedDeviceTypes`. When `preferredPlanNumber` is supplied that plan is searched first, so a
   * product's own policy always wins over an overlapping one.
   */
  findCoveringCertificate(deviceTypeCode: string, preferredPlanNumber?: string | null): ICoveringCertificate | null {
    if (!deviceTypeCode) {
      return null;
    }

    const plans = this.plansInScope();
    const preferred = plans.filter((plan) => plan.planNumber === preferredPlanNumber);
    const ordered = [...preferred, ...plans.filter((plan) => plan.planNumber !== preferredPlanNumber)];

    for (const plan of ordered) {
      const certificate = (plan.certificates ?? []).find((candidate) => candidate.product?.allowedDeviceTypes?.includes(deviceTypeCode));
      if (certificate) {
        return { planNumber: plan.planNumber, dealerCode: plan.dealerCode, certificateNumber: certificate.certificateNumber };
      }
    }

    return null;
  }

  private placeholdersForDeviceType(deviceTypeCode: string, registered: IServiceRequestProduct[]): IServiceRequestProduct[] {
    const certificatesWithRegisteredProducts = new Set(registered.map((product) => product.certificateNumber));
    const placeholders: IServiceRequestProduct[] = [];

    for (const plan of this.plansInScope()) {
      if (plan.isMultiCertPolicy === true) {
        continue;
      }

      for (const certificate of plan.certificates ?? []) {
        if (!certificate.product?.allowedDeviceTypes?.includes(deviceTypeCode)) {
          continue;
        }
        // Skip certificates that already surfaced a real product above, and certificates that
        // carry equipment at all - those are registered, they just did not match this category.
        if (certificatesWithRegisteredProducts.has(certificate.certificateNumber) || (certificate.equipments?.length ?? 0) > 0) {
          continue;
        }

        placeholders.push({
          deviceId: `${SERVICE_REQUEST_PLACEHOLDER_ID_PREFIX}${certificate.certificateNumber}`,
          deviceTypeCode,
          registrationStatus: certificate.status,
          childCertificateNumber: certificate.certificateNumber,
          planNumber: plan.planNumber,
          dealerCode: plan.dealerCode,
          certificateNumber: certificate.certificateNumber,
          certificateProductDescription: certificate.product?.productDescription ?? '',
          isSingleCertPolicy: true,
          isPlaceholder: true,
        });
      }
    }

    return placeholders;
  }

  /**
   * Registered equipment that carries NO deviceTypeCode of its own, whose covering certificate's
   * allowedDeviceTypes includes the requested code.
   *
   * A single-cert policy can't register a brand-new item once one already exists on its
   * certificate (see PlanService.restrictAllowedDeviceTypesToRegisteredEquipment) — so an
   * untyped record is the closest thing to "a match" for a device type the customer is
   * browsing: it's the same certificate, just missing the classification. Surfacing it here
   * (instead of falling through to the manual "add a new device" flow, which this policy
   * doesn't support) is what lets the customer confirm/assign the type via the same
   * certificate-item-info dialog the dashboard already uses for exactly this case. Multi-cert
   * (MAPP) policies are excluded — those DO support registering a genuinely new item.
   */
  private untypedProductsAllowingDeviceType(deviceTypeCode: string, alreadyMatched: IServiceRequestProduct[]): IServiceRequestProduct[] {
    const matchedDeviceIds = new Set(alreadyMatched.map((product) => product.deviceId));

    return this.products().filter((product) => {
      if (product.deviceTypeCode || matchedDeviceIds.has(product.deviceId) || !product.isSingleCertPolicy) {
        return false;
      }
      return !!this.findCertificateFor(product)?.product?.allowedDeviceTypes?.includes(deviceTypeCode);
    });
  }

  /** The certificate that covers one product row, resolved via its own planNumber/certificateNumber. */
  private findCertificateFor(product: IServiceRequestProduct): Certificate | null {
    const plan = this.findPlanByNumber(product.planNumber);
    return plan?.certificates?.find((certificate) => certificate.certificateNumber === product.certificateNumber) ?? null;
  }

  private unionOf(select: (plan: PlanDetail) => string[] | undefined): string[] {
    const union = new Set<string>();
    for (const plan of this.plansInScope()) {
      for (const code of select(plan) ?? []) {
        if (code) {
          union.add(code);
        }
      }
    }
    return Array.from(union);
  }
}
